import React, { useCallback, useEffect, useRef, useState } from "react";
import {
 Badge,
 Button,
 Card,
 Col,
 Form,
 ProgressBar,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";
import {
 Bar,
 BarChart,
 CartesianGrid,
 Cell,
 Legend,
 Line,
 LineChart,
 Pie,
 PieChart,
 ResponsiveContainer,
 Sector,
 Tooltip,
 XAxis,
 YAxis,
} from "recharts";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import PermissionWrapper from "../PermissionWrapper";

const formatMoney = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 maximumFractionDigits: 0,
 }).format(Number(value || 0));

const formatNumber = (value) =>
 new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
 Number(value || 0),
 );

const formatDate = (value) =>
 value && !Number.isNaN(new Date(value).getTime())
 ? new Date(value).toLocaleDateString("en-IN")
 : "-";

const toDateInputValue = (date) => {
 const value = new Date(date);
 if (Number.isNaN(value.getTime())) return "";
 const year = value.getFullYear();
 const month = String(value.getMonth() + 1).padStart(2, "0");
 const day = String(value.getDate()).padStart(2, "0");
 return `${year}-${month}-${day}`;
};

const getPeriodRange = (period) => {
 const today = new Date();
 const start = new Date(today);
 const end = new Date(today);

 if (period === "weekly") {
 const day = today.getDay();
 const mondayOffset = day === 0 ? -6 : 1 - day;
 start.setDate(today.getDate() + mondayOffset);
 end.setTime(start.getTime());
 end.setDate(start.getDate() + 6);
 } else if (period === "half-yearly") {
 const isFirstHalf = today.getMonth() < 6;
 start.setMonth(isFirstHalf ? 0 : 6, 1);
 end.setMonth(isFirstHalf ? 5 : 11, isFirstHalf ? 30 : 31);
 } else if (period === "yearly") {
 start.setMonth(0, 1);
 end.setMonth(11, 31);
 } else {
 return { fromDate: "", toDate: "" };
 }

 start.setHours(0, 0, 0, 0);
 end.setHours(0, 0, 0, 0);
 return {
 fromDate: toDateInputValue(start),
 toDate: toDateInputValue(end),
 };
};

const buildMonthTicks = (range) => {
 if (!range) return [];
 const ticks = [];
 const cursor = new Date(range.start);
 cursor.setDate(1);
 cursor.setHours(0, 0, 0, 0);
 while (cursor.getTime() <= range.end) {
 ticks.push({
 key: cursor.toISOString(),
 left: `${Math.min(Math.max(((cursor.getTime() - range.start) / range.duration) * 100, 4), 96)}%`,
 label: cursor.toLocaleDateString("en-IN", {
 month: "short",
 year: "2-digit",
 }),
 });
 cursor.setMonth(cursor.getMonth() + 1);
 }
 return ticks.length
 ? ticks
 : [{
 key: "start",
 left: "0%",
 label: new Date(range.start).toLocaleDateString("en-IN", {
 month: "short",
 year: "2-digit",
 }),
 }];
};

const downloadBlob = (blob, fileName) => {
 const url = window.URL.createObjectURL(blob);
 const anchor = document.createElement("a");
 anchor.href = url;
 anchor.download = fileName;
 document.body.appendChild(anchor);
 anchor.click();
 anchor.remove();
 window.URL.revokeObjectURL(url);
};

const Metric = ({ label, value, tone = "primary" }) => (
 <Col xs={12} sm={6} xl={3}>
 <Card className="h-100 shadow-sm">
 <Card.Body>
 <div className="text-muted small">{label}</div>
 <h4 className={`mb-0 text-${tone}`}>{value}</h4>
 </Card.Body>
 </Card>
 </Col>
);

const StatusBadges = ({ data }) => {
 const entries = Object.entries(data || {});
 if (!entries.length) return <span className="text-muted">No data</span>;
 return entries.map(([status, count]) => (
 <Badge key={status} bg="secondary" className="me-2 mb-2 px-3 py-2">
 {status}: {count}
 </Badge>
 ));
};

const healthVariant = {
 Green: "success",
 Amber: "warning",
 Red: "danger",
};

const paymentStatusVariant = {
 Paid: "success",
 "Partially Paid": "warning",
 "Pending Bill": "danger",
};

const invoiceStatusVariant = {
 Draft: "secondary",
 Submitted: "info",
 Approved: "primary",
 Rejected: "danger",
 Paid: "success",
 "Partially Paid": "warning",
};

const chartCurrency = (value) =>
 `₹${new Intl.NumberFormat("en-IN", {
 maximumFractionDigits: 0,
 notation: Number(value || 0) >= 10000000 ? "compact" : "standard",
 }).format(Number(value || 0))}`;

const renderActivePieShape = (props) => {
 const RADIAN = Math.PI / 180;
 const {
 cx,
 cy,
 midAngle,
 innerRadius,
 outerRadius,
 startAngle,
 endAngle,
 fill,
 payload,
 value,
 percent,
 } = props;

 const sin = Math.sin(-RADIAN * midAngle);
 const cos = Math.cos(-RADIAN * midAngle);
 const highlightRadius = outerRadius + 10;
 const labelRadius = outerRadius + 22;

 const sx = cx + highlightRadius * cos;
 const sy = cy + highlightRadius * sin;
 const mx = cx + labelRadius * cos;
 const my = cy + labelRadius * sin;
 const ex = mx + (cos >= 0 ? 1 : -1) * 18;
 const ey = my;

 return (
 <g>
 <text x={cx} y={cy - 8} textAnchor="middle" fill="#1f2937" fontSize={12} fontWeight={700}>
 {payload?.name}
 </text>
 <text x={cx} y={cy + 10} textAnchor="middle" fill={fill} fontSize={12} fontWeight={600}>
 {typeof value === "number" ? value.toLocaleString("en-IN") : value}
 </text>
 <text x={cx} y={cy + 26} textAnchor="middle" fill="#6b7280" fontSize={10}>
 {Number.isFinite(percent) ? `${(percent * 100).toFixed(1)}%` : ""}
 </text>
 <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
 <circle cx={ex} cy={ey} r={2.5} fill={fill} stroke="none" />
 <Sector
 cx={cx}
 cy={cy}
 innerRadius={innerRadius}
 outerRadius={highlightRadius}
 startAngle={startAngle}
 endAngle={endAngle}
 fill={fill}
 />
 <Sector
 cx={cx}
 cy={cy}
 innerRadius={outerRadius + 2}
 outerRadius={outerRadius + 12}
 startAngle={startAngle}
 endAngle={endAngle}
 fill={fill}
 opacity={0.22}
 />
 </g>
 );
};

const ProjectMcs = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [periodFilter, setPeriodFilter] = useState("all");
 const [fromDate, setFromDate] = useState("");
 const [toDate, setToDate] = useState("");
 const [data, setData] = useState(null);
 const [initialLoading, setInitialLoading] = useState(true);
 const [refreshing, setRefreshing] = useState(false);
 const [exporting, setExporting] = useState(false);
 const [exportingMpr, setExportingMpr] = useState(false);
 const [previewingMpr, setPreviewingMpr] = useState(false);
 const [exportingExcel, setExportingExcel] = useState(false);
 const [savingSnapshot, setSavingSnapshot] = useState(false);
 const [snapshots, setSnapshots] = useState([]);
 const [activeFinancialSlice, setActiveFinancialSlice] = useState(-1);
 const [activeMaterialSlice, setActiveMaterialSlice] = useState(-1);
 const hasLoadedData = useRef(false);

 const loadSnapshots = useCallback(async () => {
 try {
 const response = await api.get(`/projects/${id}/mcs/snapshots`);
 setSnapshots(response.data?.data || []);
 } catch (error) {
 console.error("MCS snapshot history load failed:", error);
 }
 }, [id]);

 const getDateParams = useCallback(() => {
 const params = {};
 if (fromDate) params.fromDate = fromDate;
 if (toDate) params.toDate = toDate;
 return params;
 }, [fromDate, toDate]);

 const handlePeriodChange = (period) => {
 setPeriodFilter(period);
 const range = getPeriodRange(period);
 setFromDate(range.fromDate);
 setToDate(range.toDate);
 };

 const handleFromDateChange = (value) => {
 setPeriodFilter(value || toDate ? "custom" : "all");
 setFromDate(value);
 };

 const handleToDateChange = (value) => {
 setPeriodFilter(fromDate || value ? "custom" : "all");
 setToDate(value);
 };

 useEffect(() => {
 const load = async () => {
 if (fromDate && toDate && fromDate > toDate) {
 if (!hasLoadedData.current) setData(null);
 setInitialLoading(false);
 setRefreshing(false);
 return;
 }
 try {
 if (hasLoadedData.current) {
 setRefreshing(true);
 } else {
 setInitialLoading(true);
 }
 const response = await api.get(`/projects/${id}/mcs`, {
 params: getDateParams(),
 });
 const nextData = response.data?.data || null;
 setData(nextData);
 hasLoadedData.current = Boolean(nextData);
 } catch (error) {
 console.error("MCS load failed:", error);
 if (!hasLoadedData.current) setData(null);
 swal("Error", error.response?.data?.message || "Failed to load MCS", "error");
 } finally {
 setInitialLoading(false);
 setRefreshing(false);
 }
 };
 load();
 }, [id, fromDate, toDate, getDateParams]);

 useEffect(() => {
 loadSnapshots();
 }, [loadSnapshots]);

 const exportPdf = async () => {
 try {
 setExporting(true);
 const response = await api.get(`/projects/${id}/mcs/pdf`, {
 params: getDateParams(),
 responseType: "blob",
 });
 downloadBlob(
 response.data,
 `${data?.project?.projectCode || "project"}_MCS.pdf`,
 );
 } catch (error) {
 console.error("MCS PDF export failed:", error);
 swal("Error", "Failed to generate MCS PDF", "error");
 } finally {
 setExporting(false);
 }
 };

 const exportMprPdf = async () => {
 try {
 setExportingMpr(true);
 const response = await api.get(`/projects/${id}/mpr/pdf`, {
 params: getDateParams(),
 responseType: "blob",
 });
 downloadBlob(
 response.data,
 `${data?.project?.projectCode || "project"}_MPR.pdf`,
 );
 } catch (error) {
 console.error("MPR PDF export failed:", error);
 swal("Error", "Failed to generate MPR PDF", "error");
 } finally {
 setExportingMpr(false);
 }
 };

 const previewMprPdf = async () => {
 let previewUrl = "";
 const previewWindow = window.open("", "_blank");
 if (previewWindow) {
 try { previewWindow.document.title = "Loading MPR Preview..."; } catch (_) {}
 }
 try {
 setPreviewingMpr(true);
 const response = await api.get(`/projects/${id}/mpr/pdf`, {
 params: { ...getDateParams(), mode: "preview" },
 responseType: "blob",
 });
 previewUrl = window.URL.createObjectURL(response.data);
 if (previewWindow) {
 try { previewWindow.location.href = previewUrl; previewWindow.focus(); }
 catch (_) { window.location.href = previewUrl; }
 } else {
 downloadBlob(response.data, `${data?.project?.projectCode || "project"}_MPR.pdf`);
 swal("Preview blocked", "Browser blocked the preview tab, PDF downloaded instead.", "info");
 }
 } catch (error) {
 console.error("MPR PDF preview failed:", error);
 swal("Error", "Failed to preview MPR PDF", "error");
 } finally {
 setPreviewingMpr(false);
 if (previewUrl) setTimeout(() => window.URL.revokeObjectURL(previewUrl), 60000);
 }
 };

 const exportExcel = async () => {
 try {
 setExportingExcel(true);
 const response = await api.get(`/projects/${id}/mcs/excel`, {
 params: getDateParams(),
 responseType: "blob",
 });
 downloadBlob(
 response.data,
 `${data?.project?.projectCode || "project"}_MCS.xlsx`,
 );
 } catch (error) {
 console.error("MCS Excel export failed:", error);
 swal("Error", "Failed to generate MCS Excel", "error");
 } finally {
 setExportingExcel(false);
 }
 };

 const saveSnapshot = async () => {
 if (fromDate && toDate && fromDate > toDate) {
 swal("Invalid Date Range", "From date cannot be after To date", "warning");
 return;
 }

 try {
 setSavingSnapshot(true);
 const response = await api.post(
 `/projects/${id}/mcs/snapshots`,
 getDateParams(),
 );
 await loadSnapshots();
 swal(
 "Snapshot Saved",
 response.data?.message || "Monthly MCS snapshot saved successfully",
 "success",
 );
 } catch (error) {
 console.error("MCS snapshot save failed:", error);
 swal(
 "Error",
 error.response?.data?.message || "Failed to save MCS snapshot",
 "error",
 );
 } finally {
 setSavingSnapshot(false);
 }
 };

 if (initialLoading) {
 return <div className="text-center my-5"><Spinner /></div>;
 }

 if (!data) {
 return (
 <>
 <PageTitle activeMenu="MCS Report" motherMenu="Projects" />
 <Card><Card.Body className="text-center py-5">
 <p className="text-muted">MCS data not found.</p>
 <Button onClick={() => navigate("/projects")}>Back to Projects</Button>
 </Card.Body></Card>
 </>
 );
 }

 const project = data.project || {};
 const overview = data.overview || {};
 const schedule = data.schedule || {};
 const execution = data.execution || {};
 const billing = data.billing || {};
 const billingDetails = billing.details || billing.recent || [];
 const material = data.material || {};
 const inspection = data.inspection || {};
 const daily = data.dailyProgress || {};
 const handover = data.handover || {};
 const health = data.health || {};
 const timelineTasks = (schedule.tasks?.length
 ? schedule.tasks
 : [
 ...(schedule.delayedTasks || []),
 ...(schedule.activeTasks || []),
 ...(schedule.upcomingTasks || []),
 ])
 .filter((task) => task.start && task.end)
 .sort((a, b) => new Date(a.start) - new Date(b.start));
 const timelineRange = (() => {
 if (!timelineTasks.length) return null;
 const start = Math.min(...timelineTasks.map((task) => new Date(task.start).getTime()));
 const end = Math.max(...timelineTasks.map((task) => new Date(task.end).getTime()));
 return { start, end, duration: Math.max(end - start, 86400000) };
 })();
 const timelineTicks = buildMonthTicks(timelineRange);
 const projectTimelineRange = (() => {
 const start = schedule.plannedStart
 ? new Date(schedule.plannedStart).getTime()
 : timelineRange?.start;
 const end = schedule.plannedFinish
 ? new Date(schedule.plannedFinish).getTime()
 : timelineRange?.end;
 if (!start || !end) return null;
 return { start, end, duration: Math.max(end - start, 86400000) };
 })();
 const projectTimelineTicks = buildMonthTicks(projectTimelineRange);
 const projectTodayPosition =
 projectTimelineRange &&
 Date.now() >= projectTimelineRange.start &&
 Date.now() <= projectTimelineRange.end
 ? ((Date.now() - projectTimelineRange.start) / projectTimelineRange.duration) * 100
 : null;
 const progressChart = [
 { name: "Execution", progress: Number(execution.progress || 0) },
 { name: "Billing", progress: Number(billing.billedPercentage || 0) },
 { name: "Payment", progress: Number(billing.paidPercentage || 0) },
 { name: "Delivery", progress: Number(material.deliveryProgress || 0) },
 ];
 const financialChart = [
 { name: "Paid", value: Number(billing.paidAmount || 0), color: "#198754" },
 { name: "Pending", value: Number(billing.pendingAmount || 0), color: "#dc3545" },
 ];
 const materialChart = [
 { name: "Delivered", value: Number(material.deliveredQuantity || 0), color: "#0d6efd" },
 {
 name: "Balance",
 value: Math.max(
 Number(material.orderedQuantity || 0) -
 Number(material.deliveredQuantity || 0),
 0,
 ),
 color: "#f0ad4e",
 },
 ];
 const historyChart = [...snapshots]
 .sort((a, b) => new Date(a.statusDate) - new Date(b.statusDate))
 .map((snapshot) => ({
 month: snapshot.monthKey,
 Execution: Number(snapshot.overview?.executionProgress || 0),
 Billing: Number(snapshot.overview?.billingProgress || 0),
 Health: Number(snapshot.health?.score || 0),
 }));
 const hasFinancialData = financialChart.some((item) => item.value > 0);
 const hasMaterialData = materialChart.some((item) => item.value > 0);

 return (
 <>
 <style>{`
 .project-mcs-action {
 height: 38px;
 min-height: 38px;
 }

 .project-mcs-date {
 height: 38px !important;
 min-height: 38px !important;
 padding-top: 0.375rem !important;
 padding-bottom: 0.375rem !important;
 }

 .project-mcs-gantt {
 min-width: 1080px;
 background: #fff;
 }

 .project-mcs-schedule-card {
 border: 1px solid #e3eaf2;
 border-radius: 10px;
 overflow: hidden;
 }

 .project-mcs-schedule-card .card-header {
 background: #ffffff;
 border-bottom: 1px solid #e6ebf2;
 padding: 14px 20px;
 }

 .project-mcs-gantt-row {
 display: grid;
 grid-template-columns: 64px minmax(280px, 1.25fr) 84px 96px 96px 128px minmax(360px, 1fr);
 min-height: 40px;
 border-bottom: 1px solid #dfe5ec;
 }

 .project-mcs-gantt-head {
 background: #f3f7fb;
 font-weight: 700;
 color: #2c4058;
 position: sticky;
 top: 0;
 z-index: 2;
 }

 .project-mcs-gantt-cell {
 padding: 9px 10px;
 border-right: 1px solid #dfe5ec;
 overflow: hidden;
 text-overflow: ellipsis;
 white-space: nowrap;
 display: flex;
 align-items: center;
 }

 .project-mcs-gantt-task {
 font-weight: 500;
 color: #1f2d3d;
 }

 .project-mcs-gantt-chart {
 position: relative;
 min-height: 40px;
 background-image: linear-gradient(to right, #e9eef5 1px, transparent 1px);
 background-size: 120px 100%;
 }

 .project-mcs-gantt-tick {
 position: absolute;
 top: 0;
 bottom: 0;
 width: 1px;
 background: #cfd7e2;
 }

 .project-mcs-gantt-tick-label {
 position: absolute;
 top: 8px;
 transform: translateX(-50%);
 font-size: 11px;
 color: #5d6b7a;
 white-space: nowrap;
 }

 .project-mcs-gantt-bar {
 position: absolute;
 top: 12px;
 height: 15px;
 min-width: 5px;
 border-radius: 999px;
 box-shadow: 0 2px 6px rgba(15, 23, 42, .18);
 display: flex;
 align-items: center;
 justify-content: center;
 color: #ffffff;
 font-size: 10px;
 font-weight: 700;
 overflow: hidden;
 white-space: nowrap;
 }

 .project-mcs-gantt-today {
 position: absolute;
 top: 0;
 bottom: 0;
 width: 2px;
 background: #ff4d5d;
 opacity: .75;
 z-index: 1;
 }

 .project-mcs-gantt-today-label {
 position: absolute;
 top: 2px;
 transform: translateX(6px);
 color: #d92d3a;
 font-size: 10px;
 font-weight: 700;
 white-space: nowrap;
 z-index: 2;
 }

 .project-mcs-schedule-overview {
 padding: 18px 20px 18px;
 border-bottom: 1px solid #e6ebf2;
 background: #ffffff;
 }

 .project-mcs-project-track {
 position: relative;
 height: 90px;
 border: 1px solid #dbe3ed;
 border-radius: 6px;
 background: #ffffff;
 overflow: hidden;
 }

 .project-mcs-project-track::before {
 content: "";
 position: absolute;
 inset: 0;
 background-image: linear-gradient(to right, #edf2f7 1px, transparent 1px);
 background-size: 120px 100%;
 }

 .project-mcs-project-bar {
 position: absolute;
 left: 16px;
 right: 16px;
 top: 43px;
 height: 20px;
 border-radius: 999px;
 background: linear-gradient(90deg, #1678d3 0%, #198754 100%);
 box-shadow: 0 4px 10px rgba(13, 110, 253, .18);
 color: #ffffff;
 font-size: 11px;
 font-weight: 700;
 display: flex;
 align-items: center;
 justify-content: center;
 }

 .project-mcs-project-tick-label {
 position: absolute;
 top: 18px;
 transform: translateX(-50%);
 font-size: 11px;
 color: #5d6b7a;
 white-space: nowrap;
 }

 .project-mcs-project-today {
 position: absolute;
 top: 0;
 bottom: 0;
 width: 2px;
 background: #ff4d5d;
 z-index: 1;
 }

 .project-mcs-gantt-legend {
 display: flex;
 flex-wrap: wrap;
 gap: 10px;
 font-size: 11px;
 color: #5d6b7a;
 }

 .project-mcs-gantt-legend-item {
 display: inline-flex;
 align-items: center;
 gap: 5px;
 white-space: nowrap;
 }

 .project-mcs-gantt-legend-dot {
 width: 18px;
 height: 8px;
 border-radius: 999px;
 display: inline-block;
 }

 .project-mcs-gantt-status {
 display: inline-flex;
 align-items: center;
 max-width: 100%;
 padding: 4px 8px;
 border-radius: 999px;
 font-size: 11px;
 font-weight: 700;
 overflow: hidden;
 text-overflow: ellipsis;
 white-space: nowrap;
 }

 .project-mcs-gantt-status-delayed {
 background: #fce7e9;
 color: #b4232f;
 }

 .project-mcs-gantt-status-upcoming {
 background: #e7f0ff;
 color: #0d5bc7;
 }

 .project-mcs-gantt-status-active {
 background: #e7f6ef;
 color: #147548;
 }

 .project-mcs-gantt-status-hold {
 background: #fff4d6;
 color: #9a6700;
 }

 .project-mcs-gantt-status-cancelled {
 background: #eef0f2;
 color: #5f6872;
 }

 .project-mcs-chart {
 width: 100%;
 height: 270px;
 }

 .project-mcs-chart-empty {
 height: 270px;
 display: flex;
 align-items: center;
 justify-content: center;
 color: #6c757d;
 }

 @media (max-width: 767px) {
 .project-mcs-gantt {
 min-width: 1040px;
 }
 }
 `}</style>
 <PageTitle activeMenu="MCS Report" motherMenu="Projects" />

 <Card className="mb-3 shadow-sm">
 <Card.Body className="d-flex justify-content-between align-items-start flex-wrap gap-3">
 <div>
 <h4 className="fw-bold mb-1">
 {project.projectCode} - {project.projectName}
 </h4>
 <div className="text-muted">{project.client?.name || "-"}</div>
 <div className="text-muted small">{project.location || "-"}</div>
 </div>
 <div className="d-flex align-items-end flex-wrap gap-2">
 {refreshing && (
 <span className="small text-muted d-inline-flex align-items-center mb-2">
 <Spinner size="sm" className="me-2" />
 Updating...
 </span>
 )}
 <Form.Group>
 <Form.Label className="small mb-1">Period</Form.Label>
 <Form.Select
 size="sm"
 className="project-mcs-date"
 value={periodFilter}
 onChange={(event) => handlePeriodChange(event.target.value)}
 >
 <option value="all">Complete Data</option>
 <option value="weekly">Weekly</option>
 <option value="half-yearly">Half Yearly</option>
 <option value="yearly">Yearly</option>
 {periodFilter === "custom" && <option value="custom">Custom Range</option>}
 </Form.Select>
 </Form.Group>
 <Form.Group>
 <Form.Label className="small mb-1">From Date</Form.Label>
 <Form.Control
 type="date"
 size="sm"
 className="project-mcs-date"
 value={fromDate}
 max={toDate || undefined}
 onChange={(event) => handleFromDateChange(event.target.value)}
 />
 </Form.Group>
 <Form.Group>
 <Form.Label className="small mb-1">To Date</Form.Label>
 <Form.Control
 type="date"
 size="sm"
 className="project-mcs-date"
 value={toDate}
 min={fromDate || undefined}
 onChange={(event) => handleToDateChange(event.target.value)}
 />
 </Form.Group>
 <PermissionWrapper user={user} required={["edit:mcs"]}>
 <Button
 variant="primary"
 size="sm"
 className="project-mcs-action"
 onClick={saveSnapshot}
 disabled={savingSnapshot || refreshing}
 >
 {savingSnapshot ? (
 <Spinner size="sm" className="me-2" />
 ) : (
 <i className="fas fa-camera me-2" />
 )}
 {savingSnapshot ? "Saving..." : "Save Snapshot"}
 </Button>
 </PermissionWrapper>
 <Button
 variant="outline-primary"
 size="sm"
 className="project-mcs-action"
 onClick={exportPdf}
 disabled={exporting}
 >
 {exporting ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-file-pdf me-2" />}
 {exporting ? "Generating..." : "Export MCS"}
 </Button>
 <PermissionWrapper user={user} required={["read:mpr"]}>
 <Button
 variant="outline-danger"
 size="sm"
 className="project-mcs-action"
 onClick={exportMprPdf}
 disabled={exportingMpr || previewingMpr}
 >
 {exportingMpr ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-file-pdf me-2" />}
 {exportingMpr ? "Generating..." : "Export MPR"}
 </Button>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["read:mpr"]}>
 <Button
 variant="outline-info"
 size="sm"
 className="project-mcs-action"
 onClick={previewMprPdf}
 disabled={previewingMpr || exportingMpr}
 >
 {previewingMpr ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-eye me-2" />}
 {previewingMpr ? "Loading..." : "Preview MPR"}
 </Button>
 </PermissionWrapper>
 <Button
 variant="outline-success"
 size="sm"
 className="project-mcs-action"
 onClick={exportExcel}
 disabled={exportingExcel}
 >
 {exportingExcel ? <Spinner size="sm" className="me-2" /> : <i className="fas fa-file-excel me-2" />}
 MCS Excel
 </Button>
 <Button
 variant="outline-secondary"
 size="sm"
 className="project-mcs-action"
 onClick={() => navigate("/projects")}
 >
 Back
 </Button>
 </div>
 </Card.Body>
 </Card>

 <Row className="g-3 mb-3">
 <Metric
 label="Project Health"
 value={`${health.status || "-"} (${health.score || 0})`}
 tone={healthVariant[health.status] || "secondary"}
 />
 <Metric label="Execution Progress" value={`${Math.round(overview.executionProgress || 0)}%`} tone="success" />
 <Metric label="Billing Progress" value={`${Math.round(overview.billingProgress || 0)}%`} />
 <Metric label="Delayed / Overdue Tasks" value={schedule.delayedCount || 0} tone="danger" />
 </Row>

 <div className={`alert alert-${healthVariant[health.status] || "secondary"} py-2 mb-3`}>
 <strong>Health assessment:</strong>{" "}
 {(health.reasons || ["No major exceptions detected"]).join("; ")}
 </div>

 <Row className="g-3 mb-3">
 <Col xl={6}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Progress Comparison</Card.Header>
 <Card.Body>
 <div className="project-mcs-chart">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={progressChart} margin={{ top: 12, right: 12, left: -12, bottom: 4 }}>
 <CartesianGrid strokeDasharray="3 3" vertical={false} />
 <XAxis dataKey="name" />
 <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
 <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, "Progress"]} />
 <Bar dataKey="progress" radius={[4, 4, 0, 0]}>
 {["#198754", "#0d6efd", "#f0ad4e", "#20a6a6"].map((color) => (
 <Cell key={color} fill={color} />
 ))}
 </Bar>
 </BarChart>
 </ResponsiveContainer>
 </div>
 </Card.Body>
 </Card>
 </Col>
 <Col md={6} xl={3}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Billing Collection</Card.Header>
 <Card.Body>
 {hasFinancialData ? (
 <div className="project-mcs-chart">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={financialChart}
 dataKey="value"
 nameKey="name"
 innerRadius={58}
 outerRadius={88}
 paddingAngle={2}
 activeIndex={activeFinancialSlice}
 activeShape={renderActivePieShape}
 isAnimationActive
 animationBegin={150}
 animationDuration={900}
 animationEasing="ease-out"
 onMouseEnter={(_, index) => setActiveFinancialSlice(index)}
 onMouseLeave={() => setActiveFinancialSlice(-1)}
 stroke="#ffffff"
 strokeWidth={2}
 >
 {financialChart.map((item, index) => (
 <Cell
 key={item.name}
 fill={item.color}
 opacity={activeFinancialSlice === -1 || activeFinancialSlice === index ? 1 : 0.35}
 />
 ))}
 </Pie>
 <Tooltip formatter={(value) => chartCurrency(value)} />
 <Legend verticalAlign="bottom" />
 </PieChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="project-mcs-chart-empty">No billing collection data</div>
 )}
 </Card.Body>
 </Card>
 </Col>
 <Col md={6} xl={3}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Material Delivery</Card.Header>
 <Card.Body>
 {hasMaterialData ? (
 <div className="project-mcs-chart">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie
 data={materialChart}
 dataKey="value"
 nameKey="name"
 innerRadius={58}
 outerRadius={88}
 paddingAngle={2}
 activeIndex={activeMaterialSlice}
 activeShape={renderActivePieShape}
 isAnimationActive
 animationBegin={150}
 animationDuration={900}
 animationEasing="ease-out"
 onMouseEnter={(_, index) => setActiveMaterialSlice(index)}
 onMouseLeave={() => setActiveMaterialSlice(-1)}
 stroke="#ffffff"
 strokeWidth={2}
 >
 {materialChart.map((item, index) => (
 <Cell
 key={item.name}
 fill={item.color}
 opacity={activeMaterialSlice === -1 || activeMaterialSlice === index ? 1 : 0.35}
 />
 ))}
 </Pie>
 <Tooltip formatter={(value) => formatNumber(value)} />
 <Legend verticalAlign="bottom" />
 </PieChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="project-mcs-chart-empty">No material quantity data</div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Card className="mb-3 shadow-sm project-mcs-schedule-card">
 <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
 <span>Billing Details</span>
 <div>
 <StatusBadges data={billing.paymentStatusBreakdown} />
 </div>
 </Card.Header>
 <Card.Body>
 <Table responsive bordered hover size="sm" className="mb-0 align-middle">
 <thead>
 <tr>
 <th>Invoice</th>
 <th>Work Order</th>
 <th>Invoice Status</th>
 <th>Payment Status</th>
 <th className="text-end">Bill Amount</th>
 <th className="text-end">Paid</th>
 <th className="text-end">Pending</th>
 <th>Submitted</th>
 <th>Paid Date</th>
 </tr>
 </thead>
 <tbody>
 {billingDetails.map((bill) => (
 <tr key={bill._id}>
 <td>
 <strong>{bill.invoiceNumber || "-"}</strong>
 <div className="text-muted small">{formatDate(bill.createdAt)}</div>
 </td>
 <td>
 {bill.workOrder?.woNo || bill.workOrder?.woNumber || "-"}
 </td>
 <td>
 <Badge bg={invoiceStatusVariant[bill.status] || "secondary"}>
 {bill.status || "-"}
 </Badge>
 </td>
 <td>
 <Badge bg={paymentStatusVariant[bill.paymentStatus] || "secondary"}>
 {bill.paymentStatus || "-"}
 </Badge>
 </td>
 <td className="text-end">{formatMoney(bill.totalAmount)}</td>
 <td className="text-end text-success">
 {formatMoney(bill.paidAmount)}
 {Number(bill.totalDeductionAmount) > 0 && (
 <div className="badge bg-light text-danger border border-danger d-block mt-1" style={{ fontSize: "10px" }}>
 Ded: -{formatMoney(bill.totalDeductionAmount)}
 </div>
 )}
 </td>
 <td className="text-end text-danger">{formatMoney(bill.pendingAmount)}</td>
 <td>{formatDate(bill.submittedDate)}</td>
 <td>{formatDate(bill.paidDate)}</td>
 </tr>
 ))}
 {!billingDetails.length && (
 <tr>
 <td colSpan="9" className="text-center text-muted">
 No billing details available for selected period
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </Card.Body>
 </Card>

 <Card className="mb-3 shadow-sm">
 <Card.Header>Monthly Progress Trend</Card.Header>
 <Card.Body>
 {historyChart.length ? (
 <div className="project-mcs-chart">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={historyChart} margin={{ top: 12, right: 24, left: -12, bottom: 4 }}>
 <CartesianGrid strokeDasharray="3 3" vertical={false} />
 <XAxis dataKey="month" />
 <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
 <Tooltip formatter={(value, name) => [`${Number(value).toFixed(1)}${name === "Health" ? "" : "%"}`, name]} />
 <Legend />
 <Line type="monotone" dataKey="Execution" stroke="#198754" strokeWidth={3} activeDot={{ r: 5 }} />
 <Line type="monotone" dataKey="Billing" stroke="#0d6efd" strokeWidth={3} activeDot={{ r: 5 }} />
 <Line type="monotone" dataKey="Health" stroke="#f0ad4e" strokeWidth={2} strokeDasharray="5 5" />
 </LineChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="project-mcs-chart-empty">
 Save monthly snapshots to build the progress trend
 </div>
 )}
 </Card.Body>
 </Card>

 <Card className="mb-3 shadow-sm project-mcs-schedule-card">
 <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
 <span>Schedule Gantt</span>
 <div className="d-flex align-items-center flex-wrap gap-3">
 <div className="project-mcs-gantt-legend">
 <span className="project-mcs-gantt-legend-item">
 <span className="project-mcs-gantt-legend-dot" style={{ background: "#198754" }} />
 Active
 </span>
 <span className="project-mcs-gantt-legend-item">
 <span className="project-mcs-gantt-legend-dot" style={{ background: "#0d6efd" }} />
 Upcoming
 </span>
 <span className="project-mcs-gantt-legend-item">
 <span className="project-mcs-gantt-legend-dot" style={{ background: "#111827" }} />
 Delayed
 </span>
 <span className="project-mcs-gantt-legend-item">
 <span className="project-mcs-gantt-legend-dot" style={{ background: "#ff4d5d", width: 3, height: 14 }} />
 Today
 </span>
 </div>
 {timelineRange && (
 <span className="small text-muted">
 {!schedule.hasDetailedGantt ? "Work Order schedule: " : ""}
 {formatDate(timelineRange.start)} - {formatDate(timelineRange.end)}
 </span>
 )}
 </div>
 </Card.Header>
 <Card.Body className="p-0 overflow-auto">
 {timelineTasks.length ? (
 <>
 {projectTimelineRange && (
 <div className="project-mcs-schedule-overview">
 <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
 <div>
 <strong>Project Timeline</strong>
 <div className="text-muted small">
 {formatDate(projectTimelineRange.start)} - {formatDate(projectTimelineRange.end)}
 </div>
 </div>
 <Badge bg={schedule.hasDetailedGantt ? "primary" : "secondary"}>
 {schedule.hasDetailedGantt ? "Detailed Gantt" : "Work Order Schedule"}
 </Badge>
 </div>
 <div className="project-mcs-project-track">
 {projectTimelineTicks.map((tick) => (
 <span
 key={tick.key}
 className="project-mcs-project-tick-label"
 style={{ left: tick.left }}
 >
 {tick.label}
 </span>
 ))}
 {projectTodayPosition !== null && (
 <span
 className="project-mcs-project-today"
 style={{ left: `${projectTodayPosition}%` }}
 />
 )}
 <div className="project-mcs-project-bar">
 Project Duration
 </div>
 </div>
 </div>
 )}
 <div className="project-mcs-gantt">
 <div className="project-mcs-gantt-row project-mcs-gantt-head">
 <div className="project-mcs-gantt-cell">Code</div>
 <div className="project-mcs-gantt-cell">Task Name</div>
 <div className="project-mcs-gantt-cell">Duration</div>
 <div className="project-mcs-gantt-cell">Start</div>
 <div className="project-mcs-gantt-cell">Finish</div>
 <div className="project-mcs-gantt-cell">Status</div>
 <div className="project-mcs-gantt-chart">
 {timelineTicks.map((tick) => (
 <React.Fragment key={tick.key}>
 <span className="project-mcs-gantt-tick" style={{ left: tick.left }} />
 <span className="project-mcs-gantt-tick-label" style={{ left: tick.left }}>
 {tick.label}
 </span>
 </React.Fragment>
 ))}
 </div>
 </div>
 {timelineTasks.map((task) => {
 const start = new Date(task.start).getTime();
 const end = new Date(task.end).getTime();
 const left = ((start - timelineRange.start) / timelineRange.duration) * 100;
 const width = Math.max(((end - start) / timelineRange.duration) * 100, 1);
 const todayPosition =
 Date.now() >= timelineRange.start && Date.now() <= timelineRange.end
 ? ((Date.now() - timelineRange.start) / timelineRange.duration) * 100
 : null;
 const isDelayed = task.timingStatus === "Delayed/Overdue";
 const isUpcoming = ["Upcoming", "Not Started"].includes(task.timingStatus);
 const isCompleted = task.timingStatus === "Completed";
 const isOnHold = task.timingStatus === "On Hold";
 const isCancelled = task.timingStatus === "Cancelled";
 const color = isDelayed
 ? "#111827"
 : isCompleted
 ? "#198754"
 : isOnHold
 ? "#f59e0b"
 : isCancelled
 ? "#6c757d"
 : isUpcoming
 ? "#0d6efd"
 : "#20c997";
 const statusClass = isDelayed
 ? "project-mcs-gantt-status-delayed"
 : isOnHold
 ? "project-mcs-gantt-status-hold"
 : isCancelled
 ? "project-mcs-gantt-status-cancelled"
 : isUpcoming
 ? "project-mcs-gantt-status-upcoming"
 : "project-mcs-gantt-status-active";
 return (
 <div className="project-mcs-gantt-row" key={task._id}>
 <div className="project-mcs-gantt-cell">
 {task.code ? `${task.group}.${task.code}` : task.group}
 </div>
 <div className="project-mcs-gantt-cell project-mcs-gantt-task" title={task.task}>
 {task.task}
 </div>
 <div className="project-mcs-gantt-cell">{task.days || 0} days</div>
 <div className="project-mcs-gantt-cell">{formatDate(task.start)}</div>
 <div className="project-mcs-gantt-cell">{formatDate(task.end)}</div>
 <div className="project-mcs-gantt-cell">
 <span className={`project-mcs-gantt-status ${statusClass}`}>
 {task.timingStatus || "-"}
 </span>
 </div>
 <div className="project-mcs-gantt-chart">
 {todayPosition !== null && (
 <>
 <span className="project-mcs-gantt-today" style={{ left: `${todayPosition}%` }} />
 <span className="project-mcs-gantt-today-label" style={{ left: `${todayPosition}%` }}>
 Today
 </span>
 </>
 )}
 <div
 className="project-mcs-gantt-bar"
 title={`${formatDate(task.start)} - ${formatDate(task.end)}`}
 style={{ left: `${left}%`, width: `${width}%`, backgroundColor: color }}
 >
 {width > 18 ? `${formatDate(task.start)} - ${formatDate(task.end)}` : ""}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </>
 ) : (
 <div className="text-center text-muted py-4">No Gantt activities available</div>
 )}
 </Card.Body>
 </Card>

 <Row className="g-3 mb-3">
 <Col lg={6}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Progress Snapshot</Card.Header>
 <Card.Body>
 <div className="d-flex justify-content-between"><span>BOQ Execution</span><strong>{Math.round(execution.progress || 0)}%</strong></div>
 <ProgressBar now={execution.progress || 0} variant="success" className="mb-3" />
 <div className="d-flex justify-content-between"><span>Billing</span><strong>{Math.round(billing.billedPercentage || 0)}%</strong></div>
 <ProgressBar now={billing.billedPercentage || 0} className="mb-3" />
 <div className="d-flex justify-content-between"><span>Payment</span><strong>{Math.round(billing.paidPercentage || 0)}%</strong></div>
 <ProgressBar now={billing.paidPercentage || 0} variant="warning" />
 </Card.Body>
 </Card>
 </Col>
 <Col lg={6}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Project Snapshot</Card.Header>
 <Card.Body>
 <div className="d-flex justify-content-between mb-2"><span>Planned Start</span><strong>{formatDate(schedule.plannedStart)}</strong></div>
 <div className="d-flex justify-content-between mb-2"><span>Planned Finish</span><strong>{formatDate(schedule.plannedFinish)}</strong></div>
 <div className="d-flex justify-content-between mb-2"><span>Activities</span><strong>{schedule.taskCount || 0}</strong></div>
 <div className="d-flex justify-content-between mb-2"><span>BOQ Value</span><strong>{formatMoney(overview.boqValue)}</strong></div>
 <div className="d-flex justify-content-between"><span>Pending Billing</span><strong className="text-danger">{formatMoney(billing.pendingAmount)}</strong></div>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Row className="g-3 mb-3">
 <Col xl={6}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Delayed / Overdue Activities</Card.Header>
 <Card.Body>
 <Table responsive bordered hover size="sm" className="mb-0">
 <thead><tr><th>Code</th><th>Activity</th><th>Finish</th><th>Days</th></tr></thead>
 <tbody>
 {(schedule.delayedTasks || []).map((task) => (
 <tr key={task._id}>
 <td>{task.group}.{task.code}</td><td>{task.task}</td>
 <td>{formatDate(task.end)}</td><td>{task.days || 0}</td>
 </tr>
 ))}
 {!schedule.delayedTasks?.length && <tr><td colSpan="4" className="text-center text-muted">No delayed activities</td></tr>}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 <Col xl={6}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Upcoming Activities</Card.Header>
 <Card.Body>
 <Table responsive bordered hover size="sm" className="mb-0">
 <thead><tr><th>Code</th><th>Activity</th><th>Start</th><th>Days</th></tr></thead>
 <tbody>
 {(schedule.upcomingTasks || []).map((task) => (
 <tr key={task._id}>
 <td>{task.group}.{task.code}</td><td>{task.task}</td>
 <td>{formatDate(task.start)}</td><td>{task.days || 0}</td>
 </tr>
 ))}
 {!schedule.upcomingTasks?.length && <tr><td colSpan="4" className="text-center text-muted">No upcoming activities</td></tr>}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Row className="g-3 mb-3">
 <Col xl={7}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Execution By Work Package</Card.Header>
 <Card.Body>
 <Table responsive bordered hover size="sm" className="mb-0">
 <thead><tr><th>Package</th><th>Installed Qty</th><th>JMRs</th><th>Approved</th></tr></thead>
 <tbody>
 {(execution.packages || []).map((item) => (
 <tr key={item.package}>
 <td>{item.package}</td><td>{formatNumber(item.installedQuantity)}</td>
 <td>{item.sheetCount}</td><td>{item.approvedSheetCount}</td>
 </tr>
 ))}
 {!execution.packages?.length && <tr><td colSpan="4" className="text-center text-muted">No execution data</td></tr>}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 <Col xl={5}>
 <Card className="h-100 shadow-sm">
 <Card.Header>Material & Quality</Card.Header>
 <Card.Body>
 <div className="d-flex justify-content-between mb-2"><span>Purchase Orders</span><strong>{material.poCount || 0}</strong></div>
 <div className="d-flex justify-content-between mb-2"><span>PO Value</span><strong>{formatMoney(material.poValue)}</strong></div>
 <div className="d-flex justify-content-between mb-1"><span>Delivery Progress</span><strong>{Math.round(material.deliveryProgress || 0)}%</strong></div>
 <ProgressBar now={material.deliveryProgress || 0} className="mb-3" />
 <div className="d-flex justify-content-between mb-2"><span>MIR Count</span><strong>{inspection.mirCount || 0}</strong></div>
 <div className="d-flex justify-content-between"><span>Inspected Quantity</span><strong>{formatNumber(inspection.inspectedQuantity)}</strong></div>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Row className="g-3">
 <Col lg={4}>
 <Card className="h-100 shadow-sm"><Card.Header>PO Status</Card.Header><Card.Body><StatusBadges data={material.poStatusBreakdown} /></Card.Body></Card>
 </Col>
 <Col lg={4}>
 <Card className="h-100 shadow-sm"><Card.Header>MIR Status</Card.Header><Card.Body><StatusBadges data={inspection.statusBreakdown} /></Card.Body></Card>
 </Col>
 <Col lg={4}>
 <Card className="h-100 shadow-sm"><Card.Header>Completion Readiness</Card.Header><Card.Body>
 <div className="d-flex justify-content-between mb-2"><span>DPR Reports</span><strong>{daily.reportCount || 0}</strong></div>
 <div className="d-flex justify-content-between mb-2"><span>Handovers Approved</span><strong>{handover.approvedCount || 0}/{handover.count || 0}</strong></div>
 <div className="d-flex justify-content-between mb-1"><span>Checklist</span><strong>{Math.round(handover.checklistProgress || 0)}%</strong></div>
 <ProgressBar now={handover.checklistProgress || 0} variant="success" />
 </Card.Body></Card>
 </Col>
 </Row>

 <Card className="mt-3 shadow-sm">
 <Card.Header>Monthly MCS History</Card.Header>
 <Card.Body>
 <Table responsive bordered hover size="sm" className="mb-0">
 <thead>
 <tr>
 <th>Month</th>
 <th>As On</th>
 <th>Health</th>
 <th>Execution</th>
 <th>Billing</th>
 <th>Delayed</th>
 <th>Open Issues</th>
 </tr>
 </thead>
 <tbody>
 {snapshots.map((snapshot) => (
 <tr key={snapshot._id}>
 <td>{snapshot.monthKey}</td>
 <td>{formatDate(snapshot.statusDate)}</td>
 <td>
 <Badge bg={healthVariant[snapshot.health?.status] || "secondary"}>
 {snapshot.health?.status || "-"} ({snapshot.health?.score || 0})
 </Badge>
 </td>
 <td>{Math.round(snapshot.overview?.executionProgress || 0)}%</td>
 <td>{Math.round(snapshot.overview?.billingProgress || 0)}%</td>
 <td>{snapshot.schedule?.delayedCount || 0}</td>
 <td>{snapshot.overview?.openIssueCount || 0}</td>
 </tr>
 ))}
 {!snapshots.length && (
 <tr>
 <td colSpan="7" className="text-center text-muted">
 No monthly snapshots saved
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </>
 );
};

export default ProjectMcs;
